import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Row, Col, Card, Statistic, Select, Input, Space, Spin, Alert, Tabs, Button, Tag, message } from 'antd';
import { IconAlertTriangle, IconCurrencyEuro, IconDownload, IconReceipt2 } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import {
  getAdminFinanceSummary,
  getAdminFinanceTransactions,
  getAdminOvertimeCharges,
  IAdminFinanceSummary,
  IAdminFinanceTransaction,
  IAdminOvertimeCharge,
} from '../../../services/AdminService';
import { getAdminInvoices, downloadAdminInvoice } from '../../../services/InvoiceService';
import { IInvoice } from '../../../interfaces/heavyDutyMachinery/IInvoice';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import DepositsTab from './finance/DepositsTab';
import HostDepositsTab from './finance/HostDepositsTab';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;

// PartiallyRefunded belongs here even though it reads like an edge case: a
// column filter only ever shows rows matching one of its own options, so
// leaving a real status out does not merely hide a filter -- it makes those
// bookings unfindable the moment anybody filters at all.
const PAYMENT_STATUS_OPTIONS = ['Succeeded', 'PartiallyRefunded', 'Refunded'];
const INVOICE_TYPE_OPTIONS = ['HostToGuest', 'PlatformCommission'];

// Shared by the invoices table's columns and its mobile cards, so the two
// can't label the same invoice differently.
const invoiceTypeLabel = (t: TFunction, value: string): string =>
  translate(
    t,
    value === 'PlatformCommission'
      ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_PLATFORM_COMMISSION
      : TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_HOST_TO_GUEST,
  );

const invoiceKindLabel = (t: TFunction, value: string): string => {
  if (value === 'Complete') return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_COMPLETE);
  if (value === 'SimplifiedQualified') return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_SIMPLIFIED_QUALIFIED);
  return translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_KIND_SIMPLIFIED);
};

// Read-only money lens over Bookings, separate from "Reservas" (BookingsSection),
// which manages the booking lifecycle instead. Every figure here excludes
// audit bookings (internal quality-check stays, not real guest revenue) --
// see AdminFinanceController's own doc comment.
const FinanceSection: React.FC = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<IAdminFinanceSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [transactions, setTransactions] = useState<IAdminFinanceTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAdminFinanceSummary()
      .then(setSummary)
      .catch(() => setSummaryError(true))
      .finally(() => setSummaryLoading(false));
  }, []);

  const loadTransactions = useCallback(() => {
    setTransactionsLoading(true);
    setTransactionsError(false);
    getAdminFinanceTransactions(status)
      .then(setTransactions)
      .catch(() => setTransactionsError(true))
      .finally(() => setTransactionsLoading(false));
  }, [status]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const cards = summary
    ? [
        { labelKey: TRANSLATIONS.ADMIN_FINANCE_SUMMARY_REVENUE, value: `${summary.totalRevenue.toFixed(2)} €` },
        { labelKey: TRANSLATIONS.ADMIN_FINANCE_SUMMARY_PLATFORM_FEES, value: `${summary.platformFees.toFixed(2)} €` },
        { labelKey: TRANSLATIONS.ADMIN_FINANCE_SUMMARY_HOST_PAYOUTS, value: `${summary.hostPayouts.toFixed(2)} €` },
        { labelKey: TRANSLATIONS.ADMIN_FINANCE_SUMMARY_REFUNDED, value: `${summary.totalRefunded.toFixed(2)} €` },
        { labelKey: TRANSLATIONS.ADMIN_FINANCE_SUMMARY_TRANSACTION_COUNT, value: summary.transactionCount },
      ]
    : [];

  const summaryTab = (
    <div>
      {summaryLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : summaryError || !summary ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_FINANCE_LOAD_ERROR)} style={{ marginBottom: 20 }} />
      ) : (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {cards.map((card) => (
            <Col key={card.labelKey} xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <IconCurrencyEuro size={20} />
                      {translate(t, card.labelKey)}
                    </div>
                  }
                  value={card.value}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Title level={5} style={{ marginBottom: 12 }}>
        {translate(t, TRANSLATIONS.ADMIN_FINANCE_TRANSACTIONS_TITLE)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_FINANCE_STATUS_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'Succeeded', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_STATUS_SUCCEEDED) },
            { value: 'PartiallyRefunded', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_STATUS_PARTIALLY_REFUNDED) },
            { value: 'Refunded', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_STATUS_REFUNDED) },
          ]}
        />
      </Space>

      {transactionsLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : transactionsError ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_FINANCE_LOAD_ERROR)} />
      ) : transactions.length === 0 ? (
        <EmptyState
          icon={<IconReceipt2 size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_FINANCE_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_FINANCE_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminFinanceTransaction>
          rowKey="id"
          dataSource={transactions}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.heavyDutyMachineryTitle,
            subtitle: `${record.hostName} → ${record.guestName}`,
            badge: <Tag style={{ marginInlineEnd: 0 }}>{record.paymentStatus}</Tag>,
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_TOTAL), value: `${record.totalPrice.toFixed(2)} €` },
              {
                label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PLATFORM_FEE),
                value: record.platformFee != null ? `${record.platformFee.toFixed(2)} €` : '—',
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PAYOUT),
                value: record.hostPayoutAmount != null ? `${record.hostPayoutAmount.toFixed(2)} €` : '—',
              },
              { label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_DATE), value: new Date(record.createdAt).toLocaleDateString() },
              { label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PAYMENT_REF), value: record.paymentReference || '—', wide: true },
            ],
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_SPACE),
              dataIndex: 'heavyDutyMachineryTitle',
              key: 'heavyDutyMachineryTitle',
              sorter: textSorter<IAdminFinanceTransaction>('heavyDutyMachineryTitle'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
              dataIndex: 'hostName',
              key: 'hostName',
              sorter: textSorter<IAdminFinanceTransaction>('hostName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_GUEST),
              dataIndex: 'guestName',
              key: 'guestName',
              sorter: textSorter<IAdminFinanceTransaction>('guestName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_TOTAL),
              dataIndex: 'totalPrice',
              key: 'totalPrice',
              sorter: numberSorter<IAdminFinanceTransaction>('totalPrice'),
              render: (value: number) => `${value.toFixed(2)} €`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PLATFORM_FEE),
              dataIndex: 'platformFee',
              key: 'platformFee',
              sorter: numberSorter<IAdminFinanceTransaction>('platformFee'),
              render: (value?: number) => (value != null ? `${value.toFixed(2)} €` : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PAYOUT),
              dataIndex: 'hostPayoutAmount',
              key: 'hostPayoutAmount',
              sorter: numberSorter<IAdminFinanceTransaction>('hostPayoutAmount'),
              render: (value?: number) => (value != null ? `${value.toFixed(2)} €` : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_STATUS),
              dataIndex: 'paymentStatus',
              key: 'paymentStatus',
              filters: enumColumnFilters(PAYMENT_STATUS_OPTIONS, (value) => value),
              onFilter: (value, record: IAdminFinanceTransaction) => record.paymentStatus === value,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_PAYMENT_REF),
              dataIndex: 'paymentReference',
              key: 'paymentReference',
              render: (value?: string) => value || '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_DATE),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminFinanceTransaction>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
          ]}
        />
      )}
    </div>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_FINANCE)}
      </Title>

      <Tabs
        items={[
          { key: 'summary', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_TAB_SUMMARY), children: summaryTab },
          { key: 'invoices', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_TAB_INVOICES), children: <InvoicesTab /> },
          { key: 'overtime', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_TAB_OVERTIME), children: <OvertimeChargesTab /> },
          // The two balances that are NOT the platform's own money: the
          // guests' fianzas and the hosts' deposits. Both existed in full on
          // the backend and neither had a single screen.
          { key: 'deposits', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_TAB_DEPOSITS), children: <DepositsTab /> },
          { key: 'host-deposits', label: translate(t, TRANSLATIONS.ADMIN_FINANCE_TAB_HOST_DEPOSITS), children: <HostDepositsTab /> },
        ]}
      />
    </div>
  );
};

/**
 * "Facturas" tab -- browses every Invoice ever issued (see
 * AdminInvoicesController's own doc comment: nothing here is ever deleted,
 * this is the platform's full retained fiscal record for Hacienda) without
 * first having to find the specific Booking it came from.
 */
const InvoicesTab: React.FC = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [type, setType] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | undefined>(undefined);

  const loadInvoices = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminInvoices(type, search || undefined)
      .then((page) => {
        setInvoices(page.items);
        setHasMore(page.hasMore);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [type, search]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const loadMore = () => {
    const last = invoices[invoices.length - 1];
    if (!last) return;
    setLoadingMore(true);
    getAdminInvoices(type, search || undefined, last.id)
      .then((page) => {
        setInvoices((prev) => [...prev, ...page.items]);
        setHasMore(page.hasMore);
      })
      .catch(() => setError(true))
      .finally(() => setLoadingMore(false));
  };

  const handleDownload = async (invoice: IInvoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadAdminInvoice(invoice);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_ERROR));
    } finally {
      setDownloadingId(undefined);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_INVOICES_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 320, width: '100%' }}
          onSearch={setSearch}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_INVOICES_TYPE_FILTER_ALL)}
          style={{ maxWidth: 220, width: '100%' }}
          value={type}
          onChange={setType}
          options={INVOICE_TYPE_OPTIONS.map((value) => ({
            value,
            label:
              value === 'PlatformCommission'
                ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_PLATFORM_COMMISSION)
                : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TYPE_HOST_TO_GUEST),
          }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_INVOICES_LOAD_ERROR)} />
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<IconReceipt2 size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_INVOICES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_INVOICES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <>
          <AdminResponsiveList<IInvoice>
            rowKey="id"
            dataSource={invoices}
            pagination={false}
            scroll={{ x: 'max-content' }}
            toCard={(record) => ({
              key: record.id,
              title: record.invoiceNumber,
              subtitle: `${record.issuerName} → ${record.recipientName}`,
              badge: <Tag style={{ marginInlineEnd: 0 }}>{invoiceTypeLabel(t, record.type)}</Tag>,
              fields: [
                { label: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_AMOUNT), value: `${record.amount.toFixed(2)} ${record.currency}` },
                { label: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_ISSUED_AT), value: new Date(record.issuedAt).toLocaleDateString() },
                { label: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_KIND), value: invoiceKindLabel(t, record.kind), wide: true },
              ],
              actions: (
                <Button size="small" icon={<IconDownload size={14} />} loading={downloadingId === record.id} onClick={() => handleDownload(record)}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_BUTTON)}
                </Button>
              ),
            })}
            columns={[
              { title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_NUMBER), dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
              {
                title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_TYPE),
                dataIndex: 'type',
                key: 'type',
                render: (value: string) => invoiceTypeLabel(t, value),
              },
              {
                title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_KIND),
                dataIndex: 'kind',
                key: 'kind',
                render: (value: string) => invoiceKindLabel(t, value),
              },
              { title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_ISSUER), dataIndex: 'issuerName', key: 'issuerName' },
              { title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_RECIPIENT), dataIndex: 'recipientName', key: 'recipientName' },
              {
                title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_AMOUNT),
                dataIndex: 'amount',
                key: 'amount',
                render: (value: number, record: IInvoice) => `${value.toFixed(2)} ${record.currency}`,
              },
              {
                title: translate(t, TRANSLATIONS.ADMIN_INVOICES_COL_ISSUED_AT),
                dataIndex: 'issuedAt',
                key: 'issuedAt',
                render: (value: string) => new Date(value).toLocaleDateString(),
              },
              {
                title: '',
                key: 'download',
                render: (_: unknown, record: IInvoice) => (
                  <Button
                    type="text"
                    size="small"
                    icon={<IconDownload size={14} />}
                    loading={downloadingId === record.id}
                    onClick={() => handleDownload(record)}
                  />
                ),
              },
            ]}
          />
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button loading={loadingMore} onClick={loadMore}>
                {translate(t, TRANSLATIONS.ADMIN_INVOICES_LOAD_MORE)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * "Exceso de horas" tab -- Fase 3 (parte 2): every Booking whose
 * engine-hours overtime charge needs manual attention (Pending: no saved
 * payment method to charge, a booking from before that capability existed;
 * Failed: an off-session attempt that declined or needed authentication it
 * couldn't get off-session). Read-only for now, same as the rest of this
 * section -- no retry action yet, that's a later task; this is purely
 * visibility so it can be resolved by hand in the meantime.
 */
const OvertimeChargesTab: React.FC = () => {
  const { t } = useTranslation();
  const [charges, setCharges] = useState<IAdminOvertimeCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    getAdminOvertimeCharges()
      .then(setCharges)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_FINANCE_LOAD_ERROR)} />;
  }

  if (charges.length === 0) {
    return (
      <EmptyState
        icon={<IconAlertTriangle size={22} />}
        title={translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_EMPTY_TITLE)}
        description={translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_EMPTY_DESCRIPTION)}
      />
    );
  }

  return (
    <AdminResponsiveList<IAdminOvertimeCharge>
      rowKey="bookingId"
      dataSource={charges}
      pagination={{ pageSize: 20 }}
      scroll={{ x: 'max-content' }}
      toCard={(record) => ({
        key: record.bookingId,
        title: record.heavyDutyMachineryTitle,
        subtitle: `${record.hostName} → ${record.guestName}`,
        badge: (
          <Tag color={record.overtimeChargeStatus === 'Failed' ? 'red' : 'orange'} style={{ marginInlineEnd: 0 }}>
            {translate(
              t,
              record.overtimeChargeStatus === 'Failed'
                ? TRANSLATIONS.ADMIN_FINANCE_OVERTIME_STATUS_FAILED
                : TRANSLATIONS.ADMIN_FINANCE_OVERTIME_STATUS_PENDING,
            )}
          </Tag>
        ),
        fields: [
          { label: translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_COL_AMOUNT), value: `${record.overtimeAmountBilled.toFixed(2)} €` },
          { label: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_DATE), value: new Date(record.endAt).toLocaleDateString() },
        ],
        actions: (
          <Link to={`/heavy-duty-machinery/bookings/${record.bookingId}`} target="_blank" rel="noopener noreferrer">
            <Button size="small">{translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_VIEW_BOOKING)}</Button>
          </Link>
        ),
      })}
      columns={[
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_SPACE),
          dataIndex: 'heavyDutyMachineryTitle',
          key: 'heavyDutyMachineryTitle',
          sorter: textSorter<IAdminOvertimeCharge>('heavyDutyMachineryTitle'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_HOST),
          dataIndex: 'hostName',
          key: 'hostName',
          sorter: textSorter<IAdminOvertimeCharge>('hostName'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_GUEST),
          dataIndex: 'guestName',
          key: 'guestName',
          sorter: textSorter<IAdminOvertimeCharge>('guestName'),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_COL_AMOUNT),
          dataIndex: 'overtimeAmountBilled',
          key: 'overtimeAmountBilled',
          sorter: numberSorter<IAdminOvertimeCharge>('overtimeAmountBilled'),
          render: (value: number) => `${value.toFixed(2)} €`,
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_STATUS),
          dataIndex: 'overtimeChargeStatus',
          key: 'overtimeChargeStatus',
          filters: enumColumnFilters(['Pending', 'Failed'], (value) => value),
          onFilter: (value, record: IAdminOvertimeCharge) => record.overtimeChargeStatus === value,
          render: (value: string) =>
            value === 'Failed'
              ? translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_STATUS_FAILED)
              : translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_STATUS_PENDING),
        },
        {
          title: translate(t, TRANSLATIONS.ADMIN_FINANCE_COL_DATE),
          dataIndex: 'endAt',
          key: 'endAt',
          sorter: dateSorter<IAdminOvertimeCharge>('endAt'),
          render: (value: string) => new Date(value).toLocaleDateString(),
        },
        {
          title: '',
          key: 'link',
          render: (_: unknown, record: IAdminOvertimeCharge) => (
            <Link to={`/heavy-duty-machinery/bookings/${record.bookingId}`} target="_blank" rel="noopener noreferrer">
              {translate(t, TRANSLATIONS.ADMIN_FINANCE_OVERTIME_VIEW_BOOKING)}
            </Link>
          ),
        },
      ]}
    />
  );
};

export default FinanceSection;
